import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";

interface LabInfo {
  lab_name: string | null;
  location: string | null;
  contact_no: string;
  email?: string | null;
}
interface PatientInfo {
  P_name: string;
  prefix: string;
  P_code: string;
  mobile: string;
  gender?: string;
  age?: number;
  age_type?: string;
}
interface BillItem {
  id: number;
  item_name: string;
  item_type: string;
  amount: string | number;
  unit_price: string | number;
  quantity: number;
  uom: string;
  test_category: number;
}
interface BillData {
  id: number;
  billnum: string;
  bill_date: string;
  entry_time?: string;
  total: string | number;
  net_amount: string | number;
  home_visit: string | number;
  discount?: string | number;
  paid_amount: string | number;
  balance: string | number;
  payment_method: string;
  reference_no: string;
  ref_prefix: string;
  reference: string;
  bill_status: number;
}
interface ReceiptData {
  labInfo: LabInfo;
  patientInfo: PatientInfo;
  billData: BillData;
  billItems: BillItem[];
  singleCategories: { category_name: string; total_price: number; category_id: number }[];
  groupCategories: { category_name: string; amount: number; category_id: number }[];
}

// Format a date/timestamp as DD/MM/YYYY in Indian Standard Time (IST).
// "en-GB" reliably renders DD/MM/YYYY with "/" separators; the explicit
// timeZone locks it to IST regardless of the browser/server's local zone.
const formatDateIST = (value?: string | number | Date | null): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
};

// Format a time value as h:mm AM/PM in IST. If the value can't be parsed
// as a date/timestamp (e.g. it's already a plain "HH:MM" string with no
// date part), it's returned as-is rather than mangled.
const formatTimeIST = (value?: string | number | Date | null): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const PosReceipt: React.FC = () => {
  const params = useParams<{ billId?: string; id?: string }>();
  const resolvedId = params.billId || params.id || "";
  const navigate = useNavigate();

  const [data, setData]       = useState<ReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (resolvedId) fetchReceipt(resolvedId);
    else { setError("No bill ID found in URL"); setLoading(false); }
  }, [resolvedId]);

  const fetchReceipt = async (id: string) => {
    try {
      const res = await API.get(`/patients/receipt/pos/${id}`);
      if (res.data?.success && res.data?.data) {
        setData(res.data.data);
      } else {
        setError("Receipt not found");
      }
    } catch (err: any) {
      setError(`Failed to load: ${err?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  };

  const n = (v: any) => parseFloat(String(v ?? 0)) || 0;

  // ── Shared receipt content builder ─────────────────────────────────────────
  const buildReceiptData = (d: ReceiptData) => {
    const labInfo          = d.labInfo          || {};
    const patientInfo      = d.patientInfo      || {};
    const billData         = d.billData         || {};
    const billItems        = Array.isArray(d.billItems)         ? d.billItems         : [];
    const singleCategories = Array.isArray(d.singleCategories) ? d.singleCategories  : [];
    const groupCategories  = Array.isArray(d.groupCategories)  ? d.groupCategories   : [];

    const totalItems = [
      // Both 'test' and 'single' items are now returned directly in billItems
      ...billItems.filter(i => i.item_type === "test" || i.item_type === "single").map(i => ({
        name: String(i.item_name || ""), amount: n(i.amount),
      })),
      ...groupCategories.map(g  => ({ name: String(g.category_name || ""), amount: n(g.amount)      })),
    ];

    const subTotal  = totalItems.reduce((s, i) => s + i.amount, 0);
    const netTotal  = n(billData.net_amount);
    const paid      = n(billData.paid_amount);
    const balance   = n(billData.balance ?? (netTotal - paid));
    const homeVisit = n(billData.home_visit);
    const discount  = n(billData.discount);
    const balanceColor = balance > 0 ? "r-red" : "r-green";

    const itemsHtml = totalItems.map(item =>
      `<div class="r-row"><span class="r-item-name">${item.name}</span><span class="r-item-amt">₹${item.amount.toFixed(2)}</span></div>`
    ).join("");

    return {
      labInfo, patientInfo, billData, subTotal, netTotal, paid, balance,
      homeVisit, discount, balanceColor, itemsHtml,
    };
  };

  // ── Print: open new window with proper 74mm thermal CSS ────────────────────
  const handlePrint = () => {
    if (!data) return;
    const {
      labInfo, patientInfo, billData, subTotal, netTotal, paid, balance,
      homeVisit, discount, balanceColor, itemsHtml,
    } = buildReceiptData(data);

    const formattedDate = formatDateIST(billData.bill_date);
    const formattedTime = formatTimeIST(billData.entry_time);

    const printWindow = window.open("", "_blank", "width=420,height=750");
    if (!printWindow) { alert("Popup blocked! Please allow popups for this site."); return; }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>POS Receipt</title>
          <style>
            /* ── Page setup for 74mm thermal paper ── */
            @page {
              size: 74mm auto;
              margin: 0;
            }
            @media print {
              html, body {
                width: 74mm;
                margin: 0 !important;
                padding: 0 !important;
              }
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            html, body {
              font-family: 'Courier New', 'Courier', monospace;
              font-size: 11px;
              font-weight: 700;
              color: #000;
              background: white;
              width: 74mm;
              margin: 0 auto;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .receipt-wrap {
              width: 74mm;
              padding: 3mm 3mm;
              margin-bottom: 10px;
            }
            .r-center { text-align: center; }
            .r-h3 {
              font-size: 14px;
              font-weight: 900;
              text-transform: uppercase;
              margin-bottom: 2px;
              letter-spacing: 0.3px;
            }
            .r-h4 {
              font-size: 11px;
              font-weight: 700;
              color: #000;
              margin-bottom: 1px;
            }
            .r-hr {
              border: none;
              border-top: 1.5px dashed #000;
              margin: 5px 0;
            }
            .r-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 2px;
              font-size: 11px;
              font-weight: 700;
            }
            .r-lbl {
              color: #000;
              flex-shrink: 0;
              margin-right: 6px;
              white-space: nowrap;
              font-weight: 700;
            }
            .r-val {
              text-align: right;
              font-weight: 700;
              word-break: break-word;
            }
            .r-item-name {
              flex: 1;
              padding-right: 4px;
              word-break: break-word;
              font-weight: 700;
            }
            .r-item-amt {
              white-space: nowrap;
              font-weight: 900;
            }
            .r-items-hdr {
              display: flex;
              justify-content: space-between;
              font-weight: 900;
              border-bottom: 1.5px solid #000;
              padding-bottom: 3px;
              margin-bottom: 4px;
              font-size: 11px;
              letter-spacing: 0.5px;
            }
            .r-total {
              display: flex;
              justify-content: space-between;
              font-weight: 900;
              font-size: 12px;
              margin-top: 4px;
              padding-top: 4px;
              border-top: 1.5px solid #000;
            }
            .r-green  { color: #000; }
            .r-red    { color: #000; }
            .r-orange { color: #000; }
            .r-small  { font-size: 10px; color: #000; font-weight: 700; }
            strong { font-weight: 900; }

            /* ── NEW: paper feed spacer so the thermal cutter doesn't clip the last line ── */
            .r-feed {
              height: 25mm;      /* adjust this value to add more/less blank space */
              width: 100%;
            }
          </style>
        </head>
        <body>
          <div class="receipt-wrap">
            <div class="r-center">
              <div class="r-h3">${(labInfo as any).lab_name || "Health Lab"}</div>
              ${(labInfo as any).location   ? `<div class="r-h4">${(labInfo as any).location}</div>`          : ""}
              ${(labInfo as any).contact_no ? `<div class="r-h4">Ph: ${(labInfo as any).contact_no}</div>`   : ""}
              ${(labInfo as any).email      ? `<div class="r-h4">${(labInfo as any).email}</div>`             : ""}
            </div>
            <div class="r-hr"></div>
            <div>
              <strong>${patientInfo.prefix ? patientInfo.prefix + ". " : ""}${patientInfo.P_name || "—"}</strong><br/>
              <span class="r-lbl">ID: </span>${patientInfo.P_code || "—"}<br/>
              <span class="r-lbl">Mob: </span>${patientInfo.mobile || "—"}
              ${patientInfo.age != null ? `<br/><span class="r-lbl">Age: </span>${patientInfo.age} ${patientInfo.age_type || "Yrs"}${patientInfo.gender ? " | " + patientInfo.gender : ""}` : ""}
            </div>
            <div class="r-hr"></div>
            <div class="r-row"><span class="r-lbl">Bill No:</span><span class="r-val">${billData.billnum || "—"}</span></div>
            <div class="r-row"><span class="r-lbl">Date:</span><span class="r-val">${formattedDate}</span></div>
            ${formattedTime ? `<div class="r-row"><span class="r-lbl">Time:</span><span class="r-val">${formattedTime}</span></div>` : ""}
            <div class="r-row"><span class="r-lbl">Ref:</span><span class="r-val">${billData.ref_prefix || ""} ${billData.reference || ""}</span></div>
            <div class="r-row"><span class="r-lbl">Payment:</span><span class="r-val">${billData.payment_method || "—"}</span></div>
            <div class="r-hr"></div>
            <div class="r-items-hdr"><span>DESCRIPTION</span><span>AMOUNT</span></div>
            ${itemsHtml || `<div style="text-align:center;color:#000;padding:6px 0;font-weight:700;">No items</div>`}
            <div class="r-hr"></div>
            <div class="r-row"><span class="r-lbl">Sub Total</span><span class="r-val">₹${subTotal.toFixed(2)}</span></div>
            ${homeVisit > 0 ? `<div class="r-row"><span class="r-lbl">Home Visit</span><span class="r-val">₹${homeVisit.toFixed(2)}</span></div>` : ""}
            ${discount > 0  ? `<div class="r-row"><span class="r-lbl">Discount</span><span class="r-val r-green">-₹${discount.toFixed(2)}</span></div>` : ""}
            <div class="r-total"><span>NET TOTAL</span><span>₹${netTotal.toFixed(2)}</span></div>
            ${paid > 0 ? `
              <div class="r-row" style="margin-top:4px">
                <span class="r-lbl">Paid (${billData.payment_method || ""})</span>
                <span class="r-val r-green">₹${paid.toFixed(2)}</span>
              </div>
              ${billData.reference_no ? `<div class="r-row"><span class="r-lbl">Ref No:</span><span class="r-val">${billData.reference_no}</span></div>` : ""}
              <div class="r-row">
                <span class="r-lbl">Balance</span>
                <span class="r-val ${balanceColor}">₹${balance.toFixed(2)}</span>
              </div>
            ` : ""}
            <div class="r-hr"></div>
            <div class="r-center r-small" style="margin-bottom:10px">Thank you for visiting us!</div>
            <div class="r-feed"></div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() { window.close(); };
              }, 300);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", fontFamily: "monospace" }}>
        <div className="spinner-border text-primary" />
        <p style={{ marginTop: 10, marginLeft: 10 }}>Loading receipt...</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "red", fontFamily: "monospace" }}>
        ⚠ {error || "No data"}
      </div>
    );
  }

  const {
    labInfo, patientInfo, billData, subTotal, netTotal, paid, balance,
    homeVisit, discount,
  } = buildReceiptData(data);

  // ── Screen display styles (inline, AdminLTE-proof) ─────────────────────────
  const S = {
    page:       { minHeight: "100vh", display: "flex", flexDirection: "column" as const, alignItems: "center", padding: "20px", fontFamily: "'Courier New', Courier, monospace" },
    receipt:    { background: "white", width: "280px", padding: "14px 12px", border: "1px solid #ccc", fontSize: "11px", lineHeight: "1.7", color: "#222", fontWeight: 700, marginBottom: "10px" },
    center:     { textAlign: "center" as const },
    h3:         { fontSize: "13px", fontWeight: 900 as const, margin: "0 0 2px 0", textTransform: "uppercase" as const, color: "#222", letterSpacing: "0.3px" },
    h4:         { fontSize: "10px", margin: "0 0 2px 0", fontWeight: 700 as const, color: "#333" },
    hr:         { border: "none", borderTop: "1px dashed #777", margin: "6px 0" },
    row:        { display: "flex", justifyContent: "space-between", marginBottom: "2px", fontSize: "11px", color: "#222", fontWeight: 700 } as React.CSSProperties,
    lbl:        { color: "#555", flexShrink: 0, marginRight: "6px", whiteSpace: "nowrap" as const, fontWeight: 700 },
    val:        { textAlign: "right" as const, fontWeight: 700, wordBreak: "break-word" as const },
    iName:      { flex: 1, paddingRight: "4px", wordBreak: "break-word" as const, color: "#222", fontWeight: 700 },
    iAmt:       { whiteSpace: "nowrap" as const, fontWeight: 900 as const },
    iHdr:       { display: "flex", justifyContent: "space-between", fontWeight: 900 as const, borderBottom: "1px solid #ccc", paddingBottom: "3px", marginBottom: "4px", fontSize: "10px" } as React.CSSProperties,
    total:      { display: "flex", justifyContent: "space-between", fontWeight: 900 as const, fontSize: "12px", marginTop: "4px", paddingTop: "4px", borderTop: "1px solid #333" } as React.CSSProperties,
    actionBar:  { display: "flex", gap: "8px", marginTop: "14px", justifyContent: "center" },
    btnPrint:   { padding: "7px 20px", background: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", fontFamily: "sans-serif" },
    btnBack:    { padding: "7px 20px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", fontFamily: "sans-serif" },
  };

  const formattedDate = formatDateIST(billData.bill_date);
  const formattedTime = formatTimeIST(billData.entry_time);

  return (
    <div style={S.page}>
      <div>
        <div style={S.receipt}>
          {/* Lab */}
          <div style={S.center}>
            <div style={S.h3}>{(labInfo as any).lab_name || "Health Lab"}</div>
            {(labInfo as any).location   && <div style={S.h4}>{(labInfo as any).location}</div>}
            {(labInfo as any).contact_no && <div style={S.h4}>Ph: {(labInfo as any).contact_no}</div>}
            {(labInfo as any).email      && <div style={S.h4}>{(labInfo as any).email}</div>}
          </div>
          <hr style={S.hr} />

          {/* Patient */}
          <div style={{ fontSize: "11px", color: "#222", lineHeight: "1.7", fontWeight: 700 }}>
            <strong style={{ fontWeight: 900 }}>{patientInfo.prefix ? `${patientInfo.prefix}. ` : ""}{patientInfo.P_name || "—"}</strong><br />
            <span style={S.lbl}>ID: </span>{patientInfo.P_code || "—"}<br />
            <span style={S.lbl}>Mob: </span>{patientInfo.mobile || "—"}
            {patientInfo.age != null && (<><br /><span style={S.lbl}>Age: </span>{patientInfo.age} {patientInfo.age_type || "Yrs"}{patientInfo.gender ? ` | ${patientInfo.gender}` : ""}</>)}
          </div>
          <hr style={S.hr} />

          {/* Bill info */}
          <div style={S.row}><span style={S.lbl}>Bill No:</span><span style={S.val}>{billData.billnum || "—"}</span></div>
          <div style={S.row}><span style={S.lbl}>Date:</span><span style={S.val}>{formattedDate}</span></div>
          {formattedTime && <div style={S.row}><span style={S.lbl}>Time:</span><span style={S.val}>{formattedTime}</span></div>}
          <div style={S.row}><span style={S.lbl}>Ref:</span><span style={S.val}>{billData.ref_prefix || ""} {billData.reference || ""}</span></div>
          <div style={S.row}><span style={S.lbl}>Payment:</span><span style={S.val}>{billData.payment_method || "—"}</span></div>
          <hr style={S.hr} />

          {/* Items */}
          <div style={S.iHdr}><span>DESCRIPTION</span><span>AMOUNT</span></div>
          {(() => {
            const billItems        = Array.isArray(data.billItems)         ? data.billItems         : [];
            const singleCategories = Array.isArray(data.singleCategories) ? data.singleCategories  : [];
            const groupCategories  = Array.isArray(data.groupCategories)  ? data.groupCategories   : [];
            const totalItems = [
              // Both 'test' and 'single' items are now returned directly in billItems
              ...billItems.filter(i => i.item_type === "test" || i.item_type === "single").map(i => ({ name: String(i.item_name || ""), amount: n(i.amount) })),
              ...groupCategories.map(g  => ({ name: String(g.category_name || ""), amount: n(g.amount) })),
            ];
            return totalItems.length === 0
              ? <div style={{ textAlign: "center", color: "#999", padding: "6px 0", fontSize: "11px" }}>No items</div>
              : totalItems.map((item, idx) => (
                <div key={idx} style={S.row}>
                  <span style={S.iName}>{item.name}</span>
                  <span style={S.iAmt}>₹{item.amount.toFixed(2)}</span>
                </div>
              ));
          })()}
          <hr style={S.hr} />

          {/* Totals */}
          <div style={S.row}><span style={S.lbl}>Sub Total</span><span style={S.val}>₹{subTotal.toFixed(2)}</span></div>
          {homeVisit > 0 && <div style={S.row}><span style={S.lbl}>Home Visit</span><span style={S.val}>₹{homeVisit.toFixed(2)}</span></div>}
          {discount  > 0 && <div style={S.row}><span style={S.lbl}>Discount</span><span style={{ ...S.val, color: "green" }}>-₹{discount.toFixed(2)}</span></div>}
          <div style={S.total}><span>NET TOTAL</span><span>₹{netTotal.toFixed(2)}</span></div>
          {paid > 0 && (<>
            <div style={{ ...S.row, marginTop: "4px" }}>
              <span style={S.lbl}>Paid ({billData.payment_method || ""})</span>
              <span style={{ ...S.val, color: "green" }}>₹{paid.toFixed(2)}</span>
            </div>
            {billData.reference_no && <div style={S.row}><span style={S.lbl}>Ref No:</span><span style={S.val}>{billData.reference_no}</span></div>}
            <div style={S.row}>
              <span style={S.lbl}>Balance</span>
              <span style={{ ...S.val, color: balance > 0 ? "red" : "green" }}>₹{balance.toFixed(2)}</span>
            </div>
          </>)}
          <hr style={S.hr} />

          <hr style={S.hr} />
          <div style={{ ...S.center, fontSize: "10px", color: "#555", fontWeight: 700 }}>Thank you for visiting us!</div>
          {/* NEW: spacer on screen too, so preview matches the printed feed gap */}
          <div style={{ height: "24px" }} />
        </div>

        {/* Buttons */}
        <div style={S.actionBar}>
          <button style={S.btnPrint} onClick={handlePrint}>🖨 Print Receipt</button>
          <button style={S.btnBack}  onClick={() => navigate(-1)}>← Back</button>
        </div>
      </div>
    </div>
  );
};

export default PosReceipt;